import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import './CookieNotice.css';

const STORAGE_KEY = 'taskeri_cookie_consent';
const PREFERENCES_KEY = 'taskeri_cookie_preferences';

// Analytics is ON by default (PIPEDA implied consent, Canada-only traffic) and can be
// switched off here; marketing stays opt-in.
const DEFAULT_PREFERENCES = {
  essential: true,
  functional: false,
  analytics: true,
  marketing: false,
};

export const getCookiePreferences = () => {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES;
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const CookieNotice = () => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const navigate = useNavigate();
  const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/ti-admin');

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow reopening from the Cookie Policy page ("Manage Cookie Preferences")
  useEffect(() => {
    const reopen = () => {
      setPreferences(getCookiePreferences());
      setShowDetails(true);
      setExiting(false);
      setVisible(true);
    };
    window.addEventListener('taskeri:open-cookie-settings', reopen);
    return () => window.removeEventListener('taskeri:open-cookie-settings', reopen);
  }, []);

  const saveAndDismiss = (type) => {
    setExiting(true);
    let prefs;
    if (type === 'accept-all') {
      prefs = { essential: true, functional: true, analytics: true, marketing: true };
    } else if (type === 'decline') {
      prefs = { essential: true, functional: false, analytics: false, marketing: false };
    } else {
      prefs = { ...preferences, essential: true };
    }
    localStorage.setItem(STORAGE_KEY, type === 'accept-all' ? 'accepted' : type === 'decline' ? 'declined' : 'custom');
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new Event('taskeri:cookie-consent'));
    // Consent audit trail (fire-and-forget, PIPEDA evidence)
    try {
      let consentId = localStorage.getItem('taskeri_consent_id');
      if (!consentId) {
        consentId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem('taskeri_consent_id', consentId);
      }
      axios.post(`${BACKEND_URL}/api/user/consent-log`, {
        action: type === 'accept-all' ? 'accepted' : type === 'decline' ? 'declined' : 'custom',
        preferences: prefs,
        consent_id: consentId,
      }, { withCredentials: true }).catch(() => {});
    } catch (e) { /* never block consent UX */ }
    setTimeout(() => setVisible(false), 350);
  };

  const togglePref = (key) => {
    if (key === 'essential') return;
    setPreferences(p => ({ ...p, [key]: !p[key] }));
  };

  if (!visible || isAdminRoute) return null;

  const categories = [
    { key: 'essential', label: 'Essential', desc: 'Required for authentication, security, and session management. Cannot be disabled.', locked: true },
    { key: 'functional', label: 'Functional', desc: 'Remember your preferences, language settings, and personalized features.' },
    { key: 'analytics', label: 'Analytics & Performance', desc: 'On by default. Helps us understand usage patterns to improve the platform — switch it off to opt out.' },
    { key: 'marketing', label: 'Marketing', desc: 'Deliver relevant content and measure campaign effectiveness.' },
  ];

  return (
    <div
      className={`ck-notice ${exiting ? 'ck-exit' : 'ck-enter'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ck-title"
      data-testid="cookie-notice"
    >
      <div className="ck-box">
        <div className="ck-header">
          <div className="ck-icon"><Cookie size={18} strokeWidth={2} /></div>
          <div className="ck-body">
            <p className="ck-title" id="ck-title">We value your privacy</p>
            <p className="ck-desc">
              We use cookies and similar technologies to improve your experience and analyze usage.
              Analytics cookies are on by default — you can switch them off below at any time.
              Marketing cookies are only used if you accept them.{' '}
              <button className="ck-link" onClick={() => navigate('/cookie-policy')} data-testid="cookie-learn-more">Learn more</button>
            </p>
          </div>
          <button className="ck-close" onClick={() => saveAndDismiss('decline')} aria-label="Dismiss cookie notice" data-testid="cookie-close">
            <X size={16} />
          </button>
        </div>

        <button className="ck-customize" onClick={() => setShowDetails(!showDetails)} aria-expanded={showDetails} data-testid="cookie-customize-btn">
          {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {showDetails ? 'Hide preferences' : 'Customize preferences'}
        </button>

        {showDetails && (
          <div className="ck-cats" data-testid="cookie-categories">
            {categories.map(cat => (
              <div key={cat.key} className={`ck-cat ${cat.locked ? 'ck-cat-locked' : ''}`} data-testid={`cookie-cat-${cat.key}`}>
                <div className="ck-cat-info">
                  <span className="ck-cat-name">{cat.label}</span>
                  <span className="ck-cat-desc">{cat.desc}</span>
                </div>
                <button
                  className={`ck-sw ${preferences[cat.key] ? 'ck-sw-on' : ''} ${cat.locked ? 'ck-sw-locked' : ''}`}
                  onClick={() => togglePref(cat.key)}
                  role="switch"
                  aria-checked={preferences[cat.key]}
                  aria-label={`${cat.label} cookies ${preferences[cat.key] ? 'enabled' : 'disabled'}`}
                  disabled={cat.locked}
                  data-testid={`cookie-toggle-${cat.key}`}
                >
                  <span className="ck-sw-thumb" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="ck-actions">
          <button className="ck-btn ck-btn-outline" onClick={() => saveAndDismiss('decline')} data-testid="cookie-decline">
            Decline All
          </button>
          {showDetails && (
            <button className="ck-btn ck-btn-secondary" onClick={() => saveAndDismiss('custom')} data-testid="cookie-save-preferences">
              Save Preferences
            </button>
          )}
          <button className="ck-btn ck-btn-primary" onClick={() => saveAndDismiss('accept-all')} data-testid="cookie-accept">
            Accept All
          </button>
        </div>
      </div>

    </div>
  );
};

export default CookieNotice;
